import React, { useContext, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/GridLegacy";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { bankAccountValidationSchema } from "../../constants/validationSchemas";
import { useMutationUpdatePharmacy } from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import { UNIQUE_PHARMACY } from "../../queries/pharmacy/pharmacyTypeDefs";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const DetailRow = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      py: 1.25,
      borderBottom: `1px solid ${LINE_SOFT}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: INK_SOFT,
        pt: 0.25,
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: mono ? MONO_FONT : BODY_FONT,
        fontSize: mono ? "13px" : "14px",
        fontWeight: 600,
        color: INK,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Box>
  </Box>
);

interface PayoutMethodSectionProps {
  pharmacy: any;
  pharmacyId: number;
  onUpdated?: () => void | Promise<unknown>;
}

export const PayoutMethodSection: React.FC<PayoutMethodSectionProps> = ({
  pharmacy,
  pharmacyId,
  onUpdated,
}) => {
  const { addToast } = useContext(ToasterContext);
  const [isEdit, setIsEdit] = useState(false);

  const payoutAccount = pharmacy?.BankAccount?.find((item: any) => item.object === "bank_account");

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy({
    refetchQueries: [
      {
        query: UNIQUE_PHARMACY,
        variables: { where: { id: pharmacyId } },
        awaitRefetchQueries: true,
      },
    ],
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(bankAccountValidationSchema),
    mode: "onChange",
  });

  const handleUpdate = async (values: any) => {
    if (!payoutAccount?.id) {
      addToast({
        type: NotificationType.Error,
        title: "Payout bank account is not available. Please contact support.",
      });
      return;
    }

    const requestData = {
      BankAccount: {
        update: {
          data: {
            accountHolderName: { set: values.accountHolderName },
            routingNumber: { set: values.routingNumber },
            last4: { set: values.accountNumber.slice(-4) },
          },
          where: { id: payoutAccount.id },
        },
      },
    };

    try {
      await updatePharmacy({
        variables: {
          data: requestData,
          accountNumber: values.accountNumber,
          where: { id: pharmacyId },
        },
      });
      addToast({
        type: NotificationType.Success,
        title: "Your payout bank account has been successfully saved",
      });
      setIsEdit(false);
      reset({
        accountHolderName: values.accountHolderName,
        routingNumber: values.routingNumber,
        accountNumber: "",
        confirmAccountNumber: "",
      });
      await onUpdated?.();
    } catch (error: any) {
      addToast({ type: NotificationType.Error, title: error.message });
    }
  };

  const cancelEdit = () => {
    reset({
      accountHolderName: payoutAccount?.accountHolderName || "",
      routingNumber: payoutAccount?.routingNumber || "",
      accountNumber: "",
      confirmAccountNumber: "",
    });
    setIsEdit(false);
  };

  return (
    <Box id="payout-method-section">
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${LINE_SOFT}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              bgcolor: "#F1F5F9",
              color: PRIMARY,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AccountBalanceIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: DISPLAY_FONT,
                fontWeight: 700,
                fontSize: 20,
                letterSpacing: "-0.01em",
                color: INK,
                lineHeight: 1.3,
              }}
            >
              Payout Method
            </Box>
            <Box sx={{ fontSize: 13, color: INK_SOFT, mt: 0.25 }}>
              Bank account for receiving sell-order payouts
            </Box>
          </Box>
        </Box>
        {payoutAccount && !isEdit && (
          <IconButton
            aria-label="Edit payout method"
            onClick={() => setIsEdit(true)}
            sx={{
              color: INK_SOFT,
              borderRadius: "8px",
              border: `1px solid ${LINE}`,
              "&:hover": { color: PRIMARY, bgcolor: "#F1F5F9", borderColor: PRIMARY },
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ p: 3, bgcolor: "#F8FAFC" }}>
        {!payoutAccount ? (
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              p: 2.5,
              fontSize: 14,
              color: INK_SOFT,
            }}
          >
            No payout bank account is on file. Please contact{" "}
            <Link href="mailto:support@primepostrx.com" underline="hover" sx={{ color: PRIMARY }}>
              support@primepostrx.com
            </Link>
            .
          </Box>
        ) : isEdit ? (
          <Box
            component="form"
            onSubmit={handleSubmit(handleUpdate)}
            noValidate
            sx={{
              bgcolor: "#FFFFFF",
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              p: { xs: 2, sm: 3 },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Account Name"
                  id="accountHolderName"
                  fullWidth
                  required
                  defaultValue={payoutAccount.accountHolderName}
                  helperText={errors.accountHolderName?.message?.toString()}
                  {...register("accountHolderName")}
                  error={!!errors.accountHolderName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Routing Number"
                  id="routingNumber"
                  fullWidth
                  required
                  defaultValue={payoutAccount.routingNumber}
                  helperText={errors.routingNumber?.message?.toString()}
                  {...register("routingNumber")}
                  error={!!errors.routingNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account Number"
                  id="accountNumber"
                  fullWidth
                  required
                  helperText={errors.accountNumber?.message?.toString()}
                  {...register("accountNumber")}
                  error={!!errors.accountNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Account Number"
                  id="confirmAccountNumber"
                  fullWidth
                  required
                  helperText={errors.confirmAccountNumber?.message?.toString()}
                  {...register("confirmAccountNumber")}
                  error={!!errors.confirmAccountNumber}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, pt: 2.5, flexWrap: "wrap" }}>
              <Button variant="outlined" sx={{ minWidth: 160 }} onClick={cancelEdit}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  minWidth: 160,
                  bgcolor: PRIMARY,
                  boxShadow: "none",
                  "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none" },
                }}
              >
                Save
                {loading && <CircularProgress sx={{ ml: 1.5 }} size={14} color="inherit" />}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                border: `1px solid ${LINE}`,
                borderRadius: "10px",
                p: 2.5,
                mb: 2,
              }}
            >
              <DetailRow label="Account holder" value={payoutAccount.accountHolderName} />
              <DetailRow label="Routing number" value={payoutAccount.routingNumber} mono />
              <DetailRow label="Account number" value={`•••• ${payoutAccount.last4}`} mono />
            </Box>

            <Box
              sx={{
                p: 2,
                border: `1px solid ${LINE}`,
                borderRadius: "10px",
                bgcolor: "#FFFFFF",
              }}
            >
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: INK_SOFT,
                  mb: 1,
                }}
              >
                Note
              </Box>
              <Box sx={{ fontSize: 14, lineHeight: 1.55, color: INK }}>
                If you update bank account information for payouts, please email{" "}
                <Link href="mailto:support@primepostrx.com" underline="hover" sx={{ color: PRIMARY, fontWeight: 600 }}>
                  support@primepostrx.com
                </Link>{" "}
                so we can verify the updated bank account details.
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PayoutMethodSection;
